export class TourPlanParameters {
    static readonly DEFAULT_SHIFT_OPTIMIZATION_UNIT = 60;

    public orderIds: number[];
    public startLocation: number | null;
    public endLocation: number | null;
    public shiftStart: string;
    public shiftEnd: string;
    public vehicleTypes: number[];
    public ignoreTimes: boolean;
    public trafficRules: TourPlanTrafficRules;
    public tourPlanningMode: TourPlanningMode;
    public followTerritoriesStrictly: boolean;
    public planOrdersAlreadyAssignedToRun?: boolean;
    public useMaximumVehicles?: boolean;
    public optimizeShiftEndTime? = false;
    public shiftOptimizationUnit?: number;
    public deleteExistingRuns? = false;
    public useBetaEnv? = false;
    public groupByLoadIntoVehicle? = false;
    public groupBySimilarOrders? = false;

    // NextBillion-only planner options (visible in Load Builder only when tenant routingProvider = NEXTBILLION).
    // Every field is optional. When omitted the backend dispatcher falls back to its pre-existing behaviour,
    // so non-NB tenants and legacy flows are never disturbed.
    public nbClusterByTerritory?: boolean;
    public nbGroupNearbyStops?: boolean;
    public nbOptimiseForDistance?: boolean;
    public nbObjectiveStrategy?: NbObjectiveStrategy;
    public nbZoneDiameterMeters?: number;
    public nbPenaltyFactor?: number;
    public nbProximityFactor?: number;
    public nbMaxVisitLatenessSeconds?: number;
    public nbMaxVehicleOvertimeSeconds?: number;
    public nbMaxTasksPerVehicle?: number;

    constructor(tourPlanParameters?: any) {
        if (tourPlanParameters) {
            this.orderIds = tourPlanParameters.orderIds ?? [];
            this.startLocation = tourPlanParameters.startLocation;
            this.endLocation = tourPlanParameters.endLocation;
            this.shiftStart = tourPlanParameters.shiftStart;
            this.shiftEnd = tourPlanParameters.shiftEnd;
            this.vehicleTypes = tourPlanParameters.vehicleTypes ?? [];
            this.ignoreTimes = tourPlanParameters.ignoreTimes === true;
            this.trafficRules = tourPlanParameters.trafficRules;
            this.tourPlanningMode = tourPlanParameters.tourPlanningMode;
            this.followTerritoriesStrictly = tourPlanParameters.followTerritoriesStrictly === true;
            this.planOrdersAlreadyAssignedToRun = tourPlanParameters.planOrdersAlreadyAssignedToRun === true;
            this.useMaximumVehicles = tourPlanParameters.useMaximumVehicles === true;
            this.optimizeShiftEndTime = tourPlanParameters.optimizeShiftEndTime === true;
            this.shiftOptimizationUnit = tourPlanParameters.shiftOptimizationUnit ?? TourPlanParameters.DEFAULT_SHIFT_OPTIMIZATION_UNIT;
            this.useBetaEnv = tourPlanParameters.useBetaEnv === true;
            this.deleteExistingRuns = tourPlanParameters.deleteExistingRuns === true;
            this.useBetaEnv = tourPlanParameters.useBetaEnv ?? false;
            this.groupByLoadIntoVehicle = tourPlanParameters.groupByLoadIntoVehicle ?? false;
            this.groupBySimilarOrders = tourPlanParameters.groupBySimilarOrders ?? false;

            // NextBillion overrides: only copy when the key is actually present on the input so we
            // never coerce undefined -> false (which the backend would treat as an explicit "off"
            // override and disable its own defaults).
            if (tourPlanParameters.nbClusterByTerritory !== undefined) this.nbClusterByTerritory = tourPlanParameters.nbClusterByTerritory;
            if (tourPlanParameters.nbGroupNearbyStops !== undefined) this.nbGroupNearbyStops = tourPlanParameters.nbGroupNearbyStops;
            if (tourPlanParameters.nbOptimiseForDistance !== undefined) this.nbOptimiseForDistance = tourPlanParameters.nbOptimiseForDistance;
            if (tourPlanParameters.nbObjectiveStrategy !== undefined) this.nbObjectiveStrategy = tourPlanParameters.nbObjectiveStrategy;
            if (tourPlanParameters.nbZoneDiameterMeters !== undefined) this.nbZoneDiameterMeters = tourPlanParameters.nbZoneDiameterMeters;
            if (tourPlanParameters.nbPenaltyFactor !== undefined) this.nbPenaltyFactor = tourPlanParameters.nbPenaltyFactor;
            if (tourPlanParameters.nbProximityFactor !== undefined) this.nbProximityFactor = tourPlanParameters.nbProximityFactor;
            if (tourPlanParameters.nbMaxVisitLatenessSeconds !== undefined)
                this.nbMaxVisitLatenessSeconds = tourPlanParameters.nbMaxVisitLatenessSeconds;
            if (tourPlanParameters.nbMaxVehicleOvertimeSeconds !== undefined)
                this.nbMaxVehicleOvertimeSeconds = tourPlanParameters.nbMaxVehicleOvertimeSeconds;
            if (tourPlanParameters.nbMaxTasksPerVehicle !== undefined) this.nbMaxTasksPerVehicle = tourPlanParameters.nbMaxTasksPerVehicle;
        }
    }
}

export enum TourPlanTrafficRules {
    LIVEORHISTORICAL,
    HISTORICALONLY,
    AUTOMATIC
} // must be CAPS
export enum TourPlanningMode {
    REAL_TIME,
    DEPOT_BASED
}

/**
 * Planner-selectable NextBillion VRP objective strategy. Mirrors the backend enum
 * {@code ae.datainsights.hm.domain.enumeration.NbObjectiveStrategy}.
 */
export enum NbObjectiveStrategy {
    DEFAULT = 'DEFAULT',
    MIN_VEHICLES = 'MIN_VEHICLES',
    BALANCE_TASKS = 'BALANCE_TASKS'
}
